import React from 'react';
import { CheckCircle, ArrowRight, Sparkles, ListChecks, Lightbulb } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Helper to extract sections from markdown
function extractSections(summary) {
  // Try to split by headings or keywords
  const overviewMatch = summary.match(/^(.*?)(?=\n*Recommendations:|\n*\*\*Recommendations|\n*1\.)/is);
  const recommendationsMatch = summary.match(/(Recommendations:|\*\*Recommendations:?\*\*)([\s\S]*?)(?=\n*Next Step:|\n*\*\*Next Step|$)/is);
  const nextStepMatch = summary.match(/(Next Step:|\*\*Next Step:?\*\*)([\s\S]*)/is);

  return {
    overview: overviewMatch ? overviewMatch[1].trim() : '',
    recommendations: recommendationsMatch ? recommendationsMatch[2].trim() : '',
    nextStep: nextStepMatch ? nextStepMatch[2].trim() : '',
  };
}

const QuizSummary = ({ summary, userData }) => {
  const { overview, recommendations, nextStep } = extractSections(summary);

  // Try to split recommendations into checklist items
  let recItems = [];
  if (recommendations) {
    // Try to split by numbered list or bullet points
    recItems = recommendations.split(/\n\s*\d+\.\s|\n\s*\*\s/).filter(Boolean).map(s => s.trim());
    // If not split, fallback to whole recommendations
    if (recItems.length <= 1) recItems = [recommendations];
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Analysis Card */}
      <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl shadow-xl p-0 overflow-hidden mb-10 border border-green-100">
        <div className="bg-green-100/60 px-8 py-6 flex flex-col items-center border-b border-green-200">
          <CheckCircle className="w-14 h-14 text-green-500 mb-2" />
          <h2 className="text-2xl font-extrabold text-green-900 mb-1 tracking-tight">Your Personalized Analysis, {userData.name}</h2>
          <p className="text-green-700 text-base font-medium">Based on your responses as a <span className="font-semibold text-green-800">{userData.coachingFocus}</span> coach, here's what we've discovered about your business:</p>
        </div>
        {/* Overview Section */}
        {overview && (
          <div className="px-8 py-6 bg-white flex items-start gap-3">
            <Lightbulb className="w-7 h-7 text-green-400 mt-1 flex-shrink-0" />
            <div className="prose prose-green max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{overview}</ReactMarkdown>
            </div>
          </div>
        )}
        {/* Recommendations Section */}
        {recItems.length > 0 && (
          <div className="px-8 py-6 bg-green-50 border-t border-green-100">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks className="w-6 h-6 text-green-500" />
              <h3 className="text-lg font-bold text-green-800 tracking-tight">Top Recommendations</h3>
            </div>
            <ul className="space-y-4 mt-2">
              {recItems.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="mt-1"><CheckCircle className="w-5 h-5 text-green-400" /></span>
                  <div className="prose prose-green max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{item}</ReactMarkdown>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {/* Next Step Section */}
        {nextStep && (
          <div className="px-8 py-6 bg-white border-t border-green-100 flex items-start gap-3">
            <Sparkles className="w-7 h-7 text-green-400 mt-1 flex-shrink-0" />
            <div>
              <h4 className="text-green-800 font-semibold mb-1">Next Step</h4>
              <div className="prose prose-green max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{nextStep}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* CTA Section */}
      <div className="bg-green-50 rounded-xl p-8 shadow flex flex-col items-center mb-8 border border-green-100">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="w-6 h-6 text-green-500" />
          <h3 className="text-xl font-bold text-green-800">Ready to Transform Your Business?</h3>
        </div>
        <p className="text-green-700 mb-6 text-center max-w-2xl">
          Take your coaching business to the next level with <span className="font-semibold text-green-800">KinOS</span> — the all-in-one platform designed specifically for wellness coaches like you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
          <a
            href="https://usekinos.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors text-lg shadow"
          >
            Try KinOS Free
            <ArrowRight className="w-5 h-5" />
          </a>
          <button className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border border-green-500 text-green-600 rounded-lg font-semibold hover:bg-green-100 transition-colors text-lg">
            Download Full Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizSummary;